<template>
    <div class="mssc-vertical-bar__container"></div>
</template>

<script>
import * as d3 from "d3";
import _ from "lodash";
import MbcVerticalBar from "mbc/dist/mbc-vertical-bar";

const dataValidator = data => {
    const fieldsValidation = {
        name: {
            type: String,
            required: true
        },
        values: {
            type: Array,
            required: true,
            validator (values) {
                return _.every(values, v => typeof v === "number");
            }
        }
    };

    if (!data || !data.length) {
        return false;
    }

    return _.every(data, d => _
                .chain(fieldsValidation)
                .keys()
                .every(field => {
                    const validate = fieldsValidation[field];
                    const field2Validate = d[field];
                    if (validate.required && !field2Validate) {
                        return false;
                    }
                    if (typeof field2Validate !== typeof validate.type()) {
                        return false;
                    }
                    if (validate.validator && !validate.validator(field2Validate)) {
                        return false;
                    }
                    return true;
                })
                .value()
        );
};

export default {
    props: {
        // Chart general properties
        barGroupPadding: {
            type: Number,
            required: false,
            default: 3
        },
        breakpoint: {
            type: Number,
            required: false,
            default: 600
        },
        colors: {
            type: Array,
            required: false,
            default () {
                return ["#82B0CB", "#D48199"];
            },
            validator (colors) {
                if (!colors || !colors.length) {
                    return false;
                }

                return _.every(colors, c => typeof c === "string");
            }
        },
        dispatch: {
            type: Boolean,
            required: false,
            default: false
        },
        defaultBenchmarkStyle: {
            type: Boolean,
            required: false,
            default: true
        },
        stacked: {
            type: Boolean,
            required: false,
            default: false
        },

        // Inherited from Chart

        canvas: {
            type: Object,
            required: false,
            default: undefined
        },
        axes: {
            type: Object,
            required: false,
            default: undefined
        },
        scales: {
            type: Object,
            required: false,
            default: undefined
        },

        // Ticks

        ticksNumber: {
            type: Number,
            required: false,
            default: 4
        },
        tickValues: {
            type: Array,
            required: false,
            default: null,
            validator (tickValues) {
                if (!tickValues || !tickValues.length) {
                    return false;
                }

                return _.every(tickValues, typeof v === "number");
            }
        },

        // X Axis

        xAxisOrient: {
            type: String,
            required: false,
            default: "bottom",
            validator (xAxisOrient) {
                const acceptedValues = ["bottom", "top"];

                return _.includes(acceptedValues, xAxisOrient);
            }
        },

        // Y Axis
        y1Abbreviation: {
            type: String,
            required: false,
            default: null
        },
        y1AxisLabel: {
            type: String,
            required: false,
            default: null
        },
        y1AxisOrient: {
            type: String,
            required: false,
            default: "left",
            validator (y1AxisOrient) {
                const acceptedValues = ["left", "right"];

                return _.includes(acceptedValues, y1AxisOrient);
            }
        },
        y1TicksAlign: {
            type: String,
            required: false,
            default: "right",
            validator (y1TicksAlign) {
                const acceptedValues = ["left", "right"];

                return _.includes(acceptedValues, y1TicksAlign);
            }
        },
        y1TickFormat: {
            type: [String, Function],
            required: false,
            default: ",.2f"
        },
        y1TickSymbol: {
            type: String,
            required: false,
            default: "$"
        },

        // Secondary Y Axis

        y2: {
            type: Boolean,
            required: false,
            default: false
        },
        y2Abbreviation: {
            type: String,
            required: false,
            default: null
        },
        y2AxisLabel: {
            type: String,
            required: false,
            default: null
        },
        y2AxisOrient: {
            type: String,
            required: false,
            default: "left",
            validator (y2AxisOrient) {
                const acceptedValues = ["left", "right"];

                return _.includes(acceptedValues, y2AxisOrient);
            }
        },
        y2TicksAlign: {
            type: String,
            required: false,
            default: "right",
            validator (y2TicksAlign) {
                const acceptedValues = ["left", "right"];

                return _.includes(acceptedValues, y2TicksAlign);
            }
        },
        y2TickFormat: {
            type: [String, Function],
            required: false,
            default: ",.2f"
        },
        y2TickSymbol: {
            type: String,
            required: false,
            default: "$"
        },
        yDomain: {
            type: Array,
            required: false,
            default: undefined,
            validator (yDomain) {
                if (!yDomain || !yDomain.length) {
                    return false;
                }

                return _.every(yDomain, v => typeof v === "number");
            }
        },

        // Y Axis shared properties

        yAxesLabelWidth: {
            type: Number,
            required: false,
            default: 15
        },
        yAxesLabelPadding: {
            type: Number,
            required: false,
            default: 5
        },
        yAxesSpacing: {
            type: Number,
            required: false,
            default: 5
        },
        yPaddingBig: {
            type: Number,
            required: false,
            default: 12
        },
        yPaddingSmall: {
            type: Number,
            required: false,
            default: 6
        },
        yTickMargin: {
            type: Number,
            required: false,
            default: 6
        },
        yTickSize: {
            type: Number,
            required: false,
            default: 35
        },

        // Benchmarks properties

        benchmaksHeight: {
            type: Number,
            required: false,
            default: 2
        },
        benchmarksColor: {
            type: Number,
            required: false,
            default: 3
        },
        benchmarksDisplayName: {
            type: String,
            required: false,
            default: "Benchmarks"
        },

        // Legend

        legend: {
            type: Boolean,
            required: false,
            default: true
        },
        legendMaxWidth: {
            type: Number,
            required: false,
            default: 120
        },
        legendPosition: {
            type: String,
            required: false,
            default: "right",
            validator (legendPosition) {
                const acceptedValues = ["right", "top"];

                return _.includes(acceptedValues, legendPosition);
            }
        },

        // dataset

        xDomain: {
            type: Array,
            required: true,
            validator (xDomain) {
                if (!xDomain || !xDomain.length) {
                    return false;
                }
                return _.every(xDomain, x => typeof x === "string");
            }
        },
        xLabels: {
            type: Array,
            required: false,
            default: undefined
        },
        y1AxisData: {
            type: Array,
            required: true,
            validator: dataValidator
        },
        y2AxisData: {
            type: Array,
            required: false,
            default: undefined,
            validator: dataValidator
        },
        Benchmarks: {
            type: Object,
            required: false,
            default: undefined,
            validator (benchmarks) {
                if (!benchmarks || (!benchmarks.values || !benchmarks.values.length)) {
                    return false;
                }

                return _.every(benchmarks.values, y => typeof y === "number");
            }
        }
    },
    data () {
        return {
            update: null
        };
    },
    computed: {
        dataset () {
            const datasetFields = ["xDomain", "y1AxisData", "y2AxisData", "Benchmarks", "xLabels"];

            return _.reduce(datasetFields, (a, v) => _.assign(a, { [v]: this[v] }), {});
        },
        options () {
            const optionsFields = [
                "barGroupPadding",
                "breakpoint",
                "colors",
                "dispatch",
                "defaultBenchmarkStyle",
                "stacked",
                "canvas",
                "axes",
                "scales",
                "ticksNumber",
                "tickValues",
                "xAxisOrient",
                "y1Abbreviation",
                "y1AxisLabel",
                "y1AxisOrient",
                "y1TicksAlign",
                "y1TickFormat",
                "y1TickSymbol",
                "y2TicksAlign",
                "y2Abbreviation",
                "y2AxisLabel",
                "y2AxisOrient",
                "y2TicksAlign",
                "y2TickFormat",
                "y2TickSymbol",
                "yDomain",
                "yAxesLabelWidth",
                "yAxesLabelPadding",
                "yAxesSpacing",
                "yPaddingBig",
                "yPaddingSmall",
                "yTickMargin",
                "yTickSize",
                "benchmaksHeight",
                "benchmarksColor",
                "benchmarksDisplayName",
                "legend",
                "legendMaxWidth",
                "legendPosition"
            ];

            return _.reduce(optionsFields, (a, v) => _.assign(a, (this[v] && { [v]: this[v] }), { legend: optionsFields.legend }), {});
        }
    },
    watch: {
        dataset () {
            this.update = null;
            window.removeEventListener("resize", () => {
                vertical.update();
                this.$emit("onRender");
            });
            const { options, dataset } = this;
            const vertical = new MbcVerticalBar(options);
            const container = d3.select(this.$el);

            setTimeout(() => {
                container.datum(dataset).call(vertical.init());
                this.$emit("onRender");
            }, 0);

            window.addEventListener("resize", () => {
                vertical.update();
                this.$emit("onRender");
            });

            this.update = data => {
                vertical.data(data);
                this.$emit("onRender");
            };
        },
    },
    mounted () {
        const { options, dataset } = this;
        const vertical = new MbcVerticalBar(options);
        const container = d3.select(this.$el);

        setTimeout(() => {
            container.datum(dataset).call(vertical.init());
            this.$emit("onRender");
        }, 0);

        window.addEventListener("resize", () => {
            vertical.update();
            this.$emit("onRender");
        });

        this.update = data => {
            vertical.data(data);
            this.$emit("onRender");
        };
    },
    methods: {
        updateChart () {
            if (this.update) {
                this.update(this.dataset);
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import '~mbc/dist/mbc-vertical-bar.css';
</style>



// WEBPACK FOOTER //
// src/components/VerticalBar.vue